import { Component, Event, EventEmitter, h, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { trapFocusInModal } from './util/trapFocusInModal';

@Component({
  tag: 'anchor-modal',
  styleUrl: './modal.scss',
  shadow: true,
})
export class Modal {
  /**
   * A unique identifier for the modal component.
   * It is used to set the id attribute of the modal element.
   * The default value is generated using the generateGuid() function.
   * */
  @Prop({ mutable: true, reflect: true }) uuid?: string = `anchor-modal-${uuidv4()}`;

  /**
   * Determines whether the close button should be displayed in the modal.
   * If true, the close button will be shown. If false, the close button will be hidden.
   * The default value is true.
   */
  @Prop() closeBtn?: boolean = true;

  /**
   * Set a text to use as the title and aria-label for close button.
   * The text will be shown on hover
   */
  @Prop({ reflect: true }) closeBtnTitle!: string;

  /**
   * Determines whether the modal is visible or hidden.
   * If true, the modal will be visible. If false, the modal will be hidden.
   * The default value is false.
   */
  @Prop({ mutable: true, reflect: true }) isVisible?: 'true' | 'false' | undefined = 'false';

  /**
   * Determines whether the focus should be trapped within the modal when it is open.
   * If true, the focus will be trapped. If false, the focus will not be trapped.
   * The default value is true.
   */
  @Prop({ reflect: true }) focusTrap?: boolean = true;

  /**
   * Determines whether the scrolling of the background content should be locked when the modal is open.
   * If true, the scrolling will be locked. If false, the scrolling will not be locked.
   * The default value is true.
   */
  @Prop({ reflect: true }) scrollLock?: boolean = true;

  /**
   * Emitted when the modal is closed.
   * The event data is set to true.
   */
  @Event() closed?: EventEmitter;

  /**
   * Emitted when the modal is opened.
   * The event data is set to true.
   */
  @Event() opened?: EventEmitter;

  @State() isOpen: boolean = false;

  /**
   * Called every time the component is connected to the DOM.
   */
  componentWillLoad() {
    this.watchIsVisible(this.isVisible);
  }

  /**
   * Closes the modal.
   * It removes the scroll lock, hides the modal, and emits the closed event with a value of true.
   */
  @Method()
  async close() {
    this.removeScrollLock();
    this.isVisible = 'false';
    this.isOpen = false;
    this.closed.emit(true);
  }

  /**
   * Opens the modal.
   * It shows the modal, emits the opened event with a value of true, and sets the scroll lock if enabled.
   */
  @Method()
  async open() {
    this.isVisible = 'true';
    this.isOpen = true;
    this.opened.emit(true);
    this.setScrollLock();
  }

  @Watch('isVisible')
  watchIsVisible(isVisible: boolean | string | undefined) {
    let shouldOpen = false;
    if (typeof isVisible === 'boolean') {
      shouldOpen = isVisible;
    }
    if (typeof isVisible === 'string') {
      shouldOpen = isVisible === 'true';
    }
    if (shouldOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  @Listen('keydown', { target: 'document' })
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      this.close();
    }
  }

  @Listen('keyup', { target: 'document' })
  handleKeyUp(ev: KeyboardEvent) {
    if (this.focusTrap) {
      trapFocusInModal({ ev, id: this.uuid });
    }
  }

  @Listen('touchend', { target: 'document' })
  handleTouchEnd(ev: TouchEvent) {
    if (this.focusTrap) {
      trapFocusInModal({ ev, id: this.uuid });
    }
  }

  setScrollLock() {
    if (this.scrollLock) {
      document.body.style.overflow = 'hidden';
    }
  }

  removeScrollLock() {
    if (this.scrollLock) {
      document.body.style.overflow = '';
    }
  }

  disconnectedCallback() {
    this.removeScrollLock();
  }

  render() {
    if (this.isOpen) {
      return (
        <div class="anchor-modal" id={this.uuid} role="dialog" aria-modal={this.isOpen}>
          <div class="anchor-modal__overlay" onClick={() => this.close()} aria-hidden="true"></div>
          <div class="anchor-modal__container">
            <div class="anchor-modal__header">
              {this.closeBtn && (
                <button class="anchor-modal__close-btn" aria-label={this.closeBtnTitle} title={this.closeBtnTitle} onClick={() => this.close()}>
                  <span class="anchor-modal__close-btn__content">
                    <slot name="close-button-before-icon"></slot>
                    <anchor-icon size="medium" name="x"></anchor-icon>
                    <slot name="close-button-after-icon"></slot>
                  </span>
                </button>
              )}
            </div>
            <div class="anchor-modal__content">
              <slot></slot>
            </div>
          </div>
        </div>
      );
    }
    return false;
  }
}
